// Course library + multi-course resolution — pure, unit-testable. Builds on the
// existing handicap model: a tee carries rating/slope/par (+ per-hole pars and
// stroke index); handicap/net math reuses handicap.js. No I/O.
import { DEFAULT_PARS } from './scoring.js'
import { DEFAULT_STROKE_INDEX, calcCourseHandicap } from './handicapCalc.js'

// Normalize a stored course to { name, id, tees:[{name,color,rating,slope,par,
// pars[18]?, strokeIndex[18]?}] }. Back-compat: the old shape stored teeBoxes
// with only name/rating/slope/par (no per-hole data) — carried through as-is.
export function normalizeCourse(raw) {
  if (!raw) return { name: '', id: null, tees: [] }
  const tees = Array.isArray(raw.tees) && raw.tees.length
    ? raw.tees
    : (raw.teeBoxes || []).map((t) => ({ name: t.name, color: t.color || '', rating: Number(t.rating) || 0, slope: Number(t.slope) || 0, par: Number(t.par) || Number(raw.coursePar) || 72, pars: t.pars || null, strokeIndex: t.strokeIndex || null }))
  return { name: raw.name || raw.courseName || '', id: raw.courseId || raw.id || null, tees: tees.map(normalizeTee) }
}
function normalizeTee(t) {
  const par18 = Array.isArray(t.pars) && t.pars.length === 18 ? t.pars.map(Number) : null
  return {
    name: t.name || 'Default',
    color: t.color || '',
    rating: Number(t.rating) || 0,
    slope: Number(t.slope) || 0,
    par: Number(t.par) || (par18 ? par18.reduce((a, b) => a + b, 0) : 72),
    pars: par18,
    strokeIndex: Array.isArray(t.strokeIndex) && t.strokeIndex.length === 18 ? t.strokeIndex.map(Number) : null,
  }
}

export function teeFor(course, teeName) {
  const c = normalizeCourse(course)
  return c.tees.find((t) => t.name === teeName) || c.tees[0] || null
}

// Course handicap from a specific tee (reuses handicap.js positional form).
export function courseHandicapFromTee(handicapIndex, tee) {
  if (!tee) return 0
  return calcCourseHandicap({ handicapIndex: Number(handicapIndex), slopeRating: Number(tee.slope), courseRating: Number(tee.rating), par: Number(tee.par) })
}

// A course/tee snapshot written onto a tournament (info) or a flight
// (flight.info.course) when a course is selected. Everything scoring needs.
export function teeSnapshot(courseName, tee) {
  return {
    courseName: courseName || '',
    teeName: tee?.name || '',
    courseRating: Number(tee?.rating) || null,
    slopeRating: Number(tee?.slope) || 113,
    coursePar: Number(tee?.par) || (tee?.pars ? tee.pars.reduce((a, b) => a + b, 0) : 72),
    pars: tee?.pars || null,
    strokeIndex: tee?.strokeIndex || null,
  }
}

// MULTI-COURSE resolution for a group: a per-flight course override
// (flight.info.course) wins; else the tournament's own info; else defaults.
// Returns everything scoring / handicap / tee sheet / cart signs / leaderboard
// need, so every surface resolves the SAME course per group.
export function resolveCourse(tournament, flight) {
  const src = flight?.info?.course || tournament?.info || {}
  const pars = Array.isArray(src.pars) && src.pars.length === 18 ? src.pars.map(Number) : DEFAULT_PARS
  const strokeIndex = Array.isArray(src.strokeIndex) && src.strokeIndex.length === 18 ? src.strokeIndex.map(Number) : DEFAULT_STROKE_INDEX
  return {
    pars,
    strokeIndex,
    rating: Number(src.courseRating) || null,
    slope: Number(src.slopeRating) || 113,
    par: Number(src.coursePar) || pars.reduce((a, b) => a + (b || 0), 0),
    courseName: src.courseName || src.course || '',
    teeName: src.teeName || '',
  }
}

// CSV / structured import (no external key needed). Lines are either:
//   key=value meta  (name=, tee=, color=, rating=, slope=)
//   data rows       "par,si"  OR  "hole,par,si"  (header row auto-skipped)
// Returns a course { name, tees:[one tee] } ready for saveClubCourse.
export function parseCourseCsv(text, defaults = {}) {
  const meta = { name: defaults.name || 'Imported Course', tee: defaults.teeName || 'Default', color: defaults.color || '', rating: Number(defaults.rating) || 0, slope: Number(defaults.slope) || 0 }
  const pars = []
  const si = []
  for (const raw of String(text).split(/\r?\n/)) {
    const line = raw.trim()
    if (!line) continue
    const kv = line.match(/^(name|tee|color|rating|slope)\s*=\s*(.+)$/i)
    if (kv) { meta[kv[1].toLowerCase()] = /rating|slope/i.test(kv[1]) ? Number(kv[2]) || 0 : kv[2].trim(); continue }
    const cells = line.split(/[,\t;]/).map((c) => c.trim())
    const nums = cells.map(Number)
    // header or junk → skip if the "par" cell isn't a sane par.
    const par = cells.length >= 3 ? nums[1] : nums[0]
    const idx = cells.length >= 3 ? nums[2] : nums[1]
    if (Number.isFinite(par) && par >= 2 && par <= 7) {
      pars.push(par)
      si.push(Number.isFinite(idx) && idx >= 1 && idx <= 18 ? idx : pars.length)
    }
  }
  const tee = {
    name: meta.tee, color: meta.color, rating: Number(meta.rating) || 0, slope: Number(meta.slope) || 0,
    par: pars.reduce((a, b) => a + (b || 0), 0) || 72,
    pars: pars.slice(0, 18), strokeIndex: si.slice(0, 18),
  }
  return { name: meta.name, tees: [tee] }
}
